/**
 * The shift: whether you are on the clock, whether you got there on time, and
 * what the game is allowed to do to you about it.
 *
 * This is the spine the rest of the systems hang off. A BEO can only be
 * accepted on the clock. Being in the lobby is only an offence on the clock.
 * Ending the day is only possible once the shift is over. So the phase this
 * module reports is read by nearly everything, and it is deliberately a small
 * enumeration rather than a bag of booleans:
 *
 *   before   the clock will not take a punch yet
 *   open     you may clock in; the shift has not started or you are inside the
 *            late window
 *   working  on the clock
 *   over     clocked out, or the shift ended while you were on it
 *   missed   the late window closed with no punch. This is a write-up, and it
 *            is issued once, by whoever is watching the phase.
 *
 * Pure: handed a clock reading and a shift definition, it answers questions.
 * It does not own the clock and it does not issue the write-up itself, because
 * a module that both decides the rule and applies the punishment is a module
 * you cannot test the rule of.
 */

import { parseTime } from './clock.js';

export const PUNCTUALITY = Object.freeze({
  ONTIME: 'on-time',
  LATE: 'late',
  MISSED: 'missed',
});

export function createShiftTracker({ schedule }) {
  /** Per-day record, keyed by day number. Survives a save. */
  let records = new Map();
  let currentDay = null;
  const listeners = new Set();

  function shiftOn(day) {
    return schedule(day);
  }

  function windowFor(day) {
    const shift = shiftOn(day);
    const start = parseTime(shift.start);
    let end = parseTime(shift.end);
    // An overnight shift ends tomorrow. Keeping end on the same numbering as
    // start, rather than wrapping it, is what lets every comparison below stay
    // a plain `<`.
    if (end <= start) end += 24 * 60;
    return {
      shift,
      start,
      end,
      opens: start - shift.openEarly,
      graceUntil: start + shift.grace,
      lateUntil: start + shift.lateWindow,
    };
  }

  function record(day) {
    let found = records.get(day);
    if (!found) {
      found = { day, clockedInAt: null, clockedOutAt: null, punctuality: null, missedFlagged: false };
      records.set(day, found);
    }
    return found;
  }

  function emit(event, payload) {
    for (const listener of listeners) listener(event, payload);
  }

  return {
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    windowFor,
    shiftOn,
    recordFor: (day) => record(day),

    /** Everything the HUD needs, in one read. */
    status(clock) {
      const day = clock.day;
      const w = windowFor(day);
      const entry = record(day);
      const now = clock.minutes;

      let phase = 'before';
      if (entry.clockedOutAt !== null) phase = 'over';
      else if (entry.clockedInAt !== null) phase = now >= w.end ? 'over' : 'working';
      else if (now >= w.lateUntil) phase = 'missed';
      else if (now >= w.opens) phase = 'open';

      return {
        day,
        shift: w.shift,
        phase,
        start: w.start,
        end: w.end,
        opens: w.opens,
        now,
        /** Negative once the shift has started. */
        untilStart: w.start - now,
        untilEnd: w.end - now,
        clockedInAt: entry.clockedInAt,
        clockedOutAt: entry.clockedOutAt,
        punctuality: entry.punctuality,
        canClockIn: entry.clockedInAt === null && now >= w.opens && now < w.end,
        canClockOut: entry.clockedInAt !== null && entry.clockedOutAt === null,
        /** True for the one frame the no-show becomes official. */
        onTheClock: phase === 'working',
      };
    },

    /**
     * Watches for the late window closing. Called every frame; fires `missed`
     * exactly once per day, whether or not the player is anywhere near the
     * alcove when it happens.
     */
    update(clock) {
      const day = clock.day;
      if (currentDay !== day) {
        currentDay = day;
        emit('day', { day, shift: shiftOn(day) });
      }
      const entry = record(day);
      if (entry.clockedInAt !== null || entry.missedFlagged) return;
      const w = windowFor(day);
      if (clock.minutes < w.lateUntil) return;
      entry.missedFlagged = true;
      entry.punctuality = PUNCTUALITY.MISSED;
      emit('missed', { day, shift: w.shift, minutesLate: clock.minutes - w.start });
    },

    /** Punches in. Returns what the clock printed on the card, or a refusal. */
    clockIn(clock) {
      const day = clock.day;
      const w = windowFor(day);
      const entry = record(day);
      const now = clock.minutes;

      if (entry.clockedInAt !== null) return { ok: false, reason: 'already-in' };
      if (now < w.opens) return { ok: false, reason: 'too-early', opensIn: w.opens - now };
      if (now >= w.end) return { ok: false, reason: 'shift-over' };

      entry.clockedInAt = now;
      // A no-show that turns up is still a no-show. The punctuality set when
      // the late window closed is not overwritten by a late punch, because the
      // write-up has already been issued and the record has to agree with it.
      if (entry.punctuality !== PUNCTUALITY.MISSED) {
        entry.punctuality = now <= w.graceUntil ? PUNCTUALITY.ONTIME : PUNCTUALITY.LATE;
      }
      const result = {
        ok: true,
        at: now,
        punctuality: entry.punctuality,
        minutesLate: Math.max(0, now - w.start),
      };
      emit('clock-in', { day, ...result });
      return result;
    },

    clockOut(clock) {
      const day = clock.day;
      const w = windowFor(day);
      const entry = record(day);
      if (entry.clockedInAt === null) return { ok: false, reason: 'never-in' };
      if (entry.clockedOutAt !== null) return { ok: false, reason: 'already-out' };

      entry.clockedOutAt = clock.minutes;
      const early = clock.minutes < w.end;
      const result = { ok: true, at: clock.minutes, early, minutesEarly: Math.max(0, w.end - clock.minutes) };
      emit('clock-out', { day, ...result });
      return result;
    },

    toJSON: () => ({ records: [...records.values()] }),
    load(state) {
      records = new Map((state?.records ?? []).map((entry) => [entry.day, entry]));
      currentDay = null;
    },
  };
}
